import classnames from 'classnames';
import { contains, filter, map } from 'ramda';
import React, { FC, useCallback } from 'react';
import { useHistory } from 'react-router';
import { MainSource } from '../../../constants/Source';
import { useMMGRSettings } from '../../../utils/hoc/withMMGRSettings';
import { encodePublicMediaFolderId } from '../../../utils/hoc/withPublicMedia/utils';
import { HomeButtonName, useTrackMediaHomeAnalytics } from '../../../utils/hooks/bi/useTrackMediaHomeAnalytics';
import { RouteBuilder } from '../../../utils/RouteHelpers';
import { discoverMediaTypesSettings } from '../discoverMediaTypesSettings';
import { HomeSliderArrows } from '../HomeSlider/HomeSliderArrows';
import { useHomeSliderControl } from '../HomeSlider/hooks/useHomeSliderControl';
import styles from './DiscoverMediaTypesSection.scss';
import { IOuter } from './DiscoverMediaTypesSection.types';
import { MediaType } from './MediaType';

export const DiscoverMediaTypesSection: FC<IOuter> = () => {
  const {
    mmgrSettings: { publicMediaRoot },
  } = useMMGRSettings();

  const mediaTypesSettings = filter((setting) => contains(setting.root, publicMediaRoot), discoverMediaTypesSettings);
  const countItems = mediaTypesSettings.length;
  const shouldDisplaySection = countItems > 1;

  const { onScrollWrapper, onScrollRight, onScrollLeft, showRightArrow, showLeftArrow, scrollingWrapper } =
    useHomeSliderControl();

  const { discoverMediaButtonClick } = useTrackMediaHomeAnalytics();

  const sliderLeftClick = useCallback(() => {
    onScrollLeft();
    discoverMediaButtonClick(HomeButtonName.NavLeft);
  }, [onScrollLeft, discoverMediaButtonClick]);

  const sliderRightClick = useCallback(() => {
    onScrollRight();
    discoverMediaButtonClick(HomeButtonName.NavRight);
  }, [onScrollRight, discoverMediaButtonClick]);

  const history = useHistory();
  const handleMediaTypeSectionClick = useCallback(
    (mediaTypeSettings) => () => {
      history.push(
        RouteBuilder.Source(
          MainSource.MediaFromWix,
          encodePublicMediaFolderId({
            categoryId: mediaTypeSettings.rootId,
            subCategoryId: 'all',
          }),
        ),
      );

      discoverMediaButtonClick(mediaTypeSettings.root);
    },
    [history, discoverMediaButtonClick],
  );

  if (!shouldDisplaySection) {
    return null;
  }

  const countItemsClassName =
    countItems === discoverMediaTypesSettings.length
      ? styles.maxCountItems
      : countItems > 4
      ? styles.moreThanFourItems
      : '';

  return (
    <div className={classnames(styles.discoverMediaTypesSection, countItemsClassName)}>
      <div className={styles.discoveryContentHolder}>
        <div className={styles.mediaTypesList} ref={scrollingWrapper} onScroll={onScrollWrapper}>
          {map(
            (mediaTypeSettings) => (
              <div
                className={styles.mediaType}
                key={mediaTypeSettings.root}
                onClick={handleMediaTypeSectionClick(mediaTypeSettings)}
                data-hook="media-type"
              >
                <MediaType settings={mediaTypeSettings} />
              </div>
            ),
            mediaTypesSettings,
          )}
        </div>

        <div className={styles.arrowsHolder}>
          <HomeSliderArrows
            sliderLeftClick={sliderLeftClick}
            sliderRightClick={sliderRightClick}
            showRightArrow={showRightArrow}
            showLeftArrow={showLeftArrow}
          />
        </div>
      </div>
    </div>
  );
};
